<template>
  <div>
    <b-row>
      <b-col cols="12">
        <div class="d-flex align-items-center mb-2" style="gap: 0.5rem;">
          <b-button
            variant="flat-secondary"
            class="btn-icon"
            @click="$router.back()"
          >
            <feather-icon icon="ArrowLeftIcon" />
          </b-button>
          <div>
            <h1 class="mb-0">
              Selection Record
            </h1>
            <p
              v-if="isLoggedIn && usersFullName"
              class="text-muted mt-1 mb-0"
            >
              Generated for {{ usersFullName }}<template v-if="companyName">
                of {{ companyName }}
              </template> on {{ generatedDate }}
            </p>
          </div>
        </div>

        <template v-if="!loading && !error">
          <b-row class="mt-2">
            <!-- Selection Details -->
            <b-col cols="12" lg="4" order="2" order-lg="2">
              <h2 class="mb-2">
                Selection Details
              </h2>
              <b-card>
                <b-form-group
                  label="Record Name"
                  label-for="record-name"
                  label-class="font-weight-bold"
                  class="mb-3"
                >
                  <b-form-input
                    id="record-name"
                    v-model="recordName"
                    placeholder="Enter a name for this record"
                  />
                </b-form-group>

                <b-form-group
                  label="Selection Reason"
                  label-for="table-reason"
                  label-class="font-weight-bold"
                  class="mb-3"
                >
                  <b-form-textarea
                    id="table-reason"
                    v-model="tableReason"
                    placeholder="Why did you select these products?"
                    rows="4"
                  />
                </b-form-group>

                <div v-if="activeFilters.length > 0" class="mb-3">
                  <h6 class="font-weight-bold mb-2">
                    Filter Criteria
                  </h6>
                  <div
                    v-for="filter in activeFilters"
                    :key="filter.key"
                    class="filter-criteria-row mb-2"
                  >
                    <div class="filter-criteria-left">
                      <span class="font-weight-bold text-primary small d-block mb-50">{{ filter.label }}</span>
                      <div class="selected-values-compact">
                        <b-badge
                          v-for="(value, index) in filter.selectedValues"
                          :key="index"
                          variant="light-primary"
                          class="mr-50 mb-50"
                          pill
                        >
                          {{ value }}
                        </b-badge>
                      </div>
                    </div>
                    <b-form-textarea
                      :id="`filter-reason-${filter.key}`"
                      v-model="filterReasons[filter.key]"
                      placeholder="Explain your choice..."
                      rows="2"
                      class="small-textarea filter-criteria-right"
                    />
                  </div>
                </div>

                <div v-if="hasActiveConstraints" class="mb-3">
                  <h6 class="font-weight-bold mb-2">
                    Date Constraints
                  </h6>
                  <label class="font-weight-bold text-primary small">
                    Applied Constraint
                  </label>
                  <div class="mb-1">
                    <b-badge variant="light-info" pill>
                      {{ constraintSummary }}
                    </b-badge>
                  </div>
                  <b-form-textarea
                    id="constraint-reason"
                    v-model="constraintReason"
                    placeholder="Explain your date constraint..."
                    rows="2"
                    class="small-textarea"
                  />
                </div>

                <div class="d-flex justify-content-center mt-1 mb-2">
                  <b-alert show variant="info" class="d-inline-block mb-0 px-3 py-75">
                    <feather-icon icon="InfoIcon" class="mr-50" />
                    To change your selection please go back to the previous screen.
                  </b-alert>
                </div>

                <div class="d-flex justify-content-end mt-2" style="gap: 0.5rem;">
                  <b-button
                    variant="outline-secondary"
                    class="d-flex align-items-center"
                    @click="$router.back()"
                  >
                    <feather-icon icon="ArrowLeftIcon" class="mr-50" />
                    Change Selection
                  </b-button>
                  <span id="generate-record-wrapper" class="d-inline-block">
                    <b-button
                      variant="primary"
                      class="d-flex align-items-center"
                      :disabled="!isFormValid"
                      :style="!isFormValid ? 'pointer-events: none;' : ''"
                      @click="saveSelectionRecord"
                    >
                      <feather-icon icon="FileTextIcon" class="mr-50" />
                      Generate Record
                    </b-button>
                  </span>
                  <b-tooltip
                    v-if="!isFormValid"
                    :key="String(isFormValid)"
                    target="generate-record-wrapper"
                    triggers="hover focus"
                    placement="top"
                    variant="warning"
                  >
                    All fields must be filled in before generating a record
                  </b-tooltip>
                </div>
              </b-card>
            </b-col>

            <!-- My Selection -->
            <b-col cols="12" lg="8" order="1" order-lg="1">
              <div class="mb-2">
                <h2 class="mb-50">
                  My Selection
                </h2>
                <div class="d-flex mb-1" style="gap: 0.5rem;">
                  <b-badge variant="light-success" pill>
                    <feather-icon icon="CheckIcon" size="11" class="mr-25" />
                    {{ activeReportCount }} products selected
                  </b-badge>
                  <b-badge variant="light-warning" pill>
                    <feather-icon icon="EyeOffIcon" size="11" class="mr-25" />
                    {{ filteredNotSelected }} products not selected
                  </b-badge>
                  <b-badge variant="light-danger" pill>
                    <feather-icon icon="XIcon" size="11" class="mr-25" />
                    {{ failedFilter }} products omitted
                  </b-badge>
                </div>
              </div>
              <b-card>
                <b-table
                  responsive
                  :items="products"
                  :fields="reportTableFields"
                  :tbody-tr-class="row => isReportDisabled(row.reportNumber) ? 'report-row-disabled' : ''"
                >
                  <template #head(provider)>
                    <div>Provider</div>
                    <div>Product</div>
                    <div>Counterparty</div>
                  </template>
                  <template #head(type)>
                    <div>Type</div>
                    <div>Underlying</div>
                    <div>Length</div>
                  </template>
                  <template #head(capital)>
                    <div>Capital</div>
                    <div>Aim</div>
                    <div>Vehicle</div>
                  </template>
                  <template #cell(reportNumber)="data">
                    <router-link :to="{ name: 'product-report', params: { reportNumber: data.item.reportNumber } }" class="text-body">
                      {{ data.item.reportNumber }}
                    </router-link>
                  </template>
                  <template #cell(closingDate)="data">
                    {{ formatIsoDate(data.item.closingDate) }}
                  </template>
                  <template #cell(provider)="data">
                    <div class="d-flex flex-column">
                      <span>{{ data.item.provider }}</span>
                      <span>{{ data.item.productName }}</span>
                      <span>{{ data.item.counterParty }}</span>
                    </div>
                  </template>
                  <template #cell(type)="data">
                    <div class="d-flex flex-column">
                      <span>{{ data.item.type }}</span>
                      <span>{{ data.item.underlying }}</span>
                      <span>{{ data.item.length }}</span>
                    </div>
                  </template>
                  <template #cell(capital)="data">
                    <div class="d-flex flex-column">
                      <span>{{ data.item.capital }}</span>
                      <span>{{ data.item.aim }}</span>
                      <span>{{ data.item.vehicle }}</span>
                    </div>
                  </template>
                </b-table>

                <div v-if="excludedProducts.length > 0" class="mt-3">
                  <b-button
                    v-b-toggle.excluded-reports-collapse
                    variant="outline-secondary"
                    size="sm"
                    block
                    class="text-left d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <feather-icon
                        icon="EyeOffIcon"
                        size="16"
                        class="mr-50"
                      />
                      Products not selected ({{ excludedProducts.length }})
                    </span>
                    <feather-icon
                      icon="ChevronDownIcon"
                      size="16"
                    />
                  </b-button>

                  <b-collapse id="excluded-reports-collapse" class="mt-2">
                    <b-table
                      v-if="excludedProducts.length > 0"
                      responsive
                      :items="excludedProducts"
                      :fields="reportTableFields"
                      tbody-tr-class="report-row-disabled"
                    >
                      <template #head(provider)>
                        <div>Provider</div>
                        <div>Product</div>
                        <div>Counterparty</div>
                      </template>
                      <template #head(type)>
                        <div>Type</div>
                        <div>Underlying</div>
                        <div>Length</div>
                      </template>
                      <template #head(capital)>
                        <div>Capital</div>
                        <div>Aim</div>
                        <div>Vehicle</div>
                      </template>
                      <template #cell(reportNumber)="data">
                        <router-link :to="{ name: 'product-report', params: { reportNumber: data.item.reportNumber } }" class="text-body">
                          {{ data.item.reportNumber }}
                        </router-link>
                      </template>
                      <template #cell(closingDate)="data">
                        {{ formatIsoDate(data.item.closingDate) }}
                      </template>
                      <template #cell(provider)="data">
                        <div class="d-flex flex-column">
                          <span>{{ data.item.provider }}</span>
                          <span>{{ data.item.productName }}</span>
                          <span>{{ data.item.counterParty }}</span>
                        </div>
                      </template>
                      <template #cell(type)="data">
                        <div class="d-flex flex-column">
                          <span>{{ data.item.type }}</span>
                          <span>{{ data.item.underlying }}</span>
                          <span>{{ data.item.length }}</span>
                        </div>
                      </template>
                      <template #cell(capital)="data">
                        <div class="d-flex flex-column">
                          <span>{{ data.item.capital }}</span>
                          <span>{{ data.item.aim }}</span>
                          <span>{{ data.item.vehicle }}</span>
                        </div>
                      </template>
                    </b-table>
                  </b-collapse>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </template>

        <div v-if="loading">
          <loading-container />
        </div>

        <no-data-container
          v-else-if="error"
          :message="errorMessage || 'No reports selected'"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BBadge, BAlert, BFormGroup, BFormInput, BFormTextarea, BCollapse, BTable, BTooltip, VBToggle,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import {
  formatIsoDate,
  formatNumberTextMaxDecimalPlaces,
} from '@/utils/formatters'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BAlert,
    BCollapse,
    BTable,
    BTooltip,
    LoadingContainer,
    NoDataContainer,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  data() {
    return {
      generatedDate: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      reportIds: [],
      products: [], // Array of full product data structs
      excludedReportIds: [], // Array of excluded report IDs
      excludedProducts: [], // Array of excluded product data structs
      disabledReports: [], // Array of disabled report IDs
      filterData: {},
      search: '',
      filterDisplay: [],
      filterSequentialCounts: {},
      totalProductCount: 0,
      constraints: {},
      recordName: '',
      tableReason: '',
      filterReasons: {},
      constraintReason: '',
      loading: false,
      error: false,
      errorMessage: '',
      reportTableFields: [
        { key: 'reportNumber', label: 'Report Number', sortable: true },
        { key: 'closingDate', label: 'Closing Date', sortable: true },
        { key: 'provider', label: 'Provider', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'capital', label: 'Capital', sortable: true },
        { key: 'score', label: 'Score', sortable: true },
        { key: 'risk', label: 'Risk', sortable: true },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'usersFullName',
    ]),
    companyName() {
      return this.$store.state.auth.user.companyname
    },
    isFormValid() {
      if (!this.recordName.trim()) return false
      if (!this.tableReason.trim()) return false
      return this.activeFilters.every(filter => this.filterReasons[filter.key] && this.filterReasons[filter.key].trim())
    },
    activeReportCount() {
      return this.reportIds.length - this.disabledReports.length
    },
    filteredNotSelected() {
      return this.excludedProducts.length
    },
    failedFilter() {
      const passedFilter = this.reportIds.length + this.excludedProducts.length
      return Math.max(0, this.totalProductCount - passedFilter)
    },
    activeFilters() {
      // Build array of active filters with their labels and selected values
      const filters = []

      if (this.search) {
        filters.push({ key: 'search', label: 'Search', selectedValues: [this.search] })
      }

      if (!this.filterData || !this.filterDisplay) {
        return filters
      }

      // Iterate through filterData to find active selections
      Object.keys(this.filterData).forEach(categoryKey => {
        const selectedValues = this.filterData[categoryKey]

        if (!selectedValues || selectedValues.length === 0) {
          return
        }

        // Find the category in filterDisplay to get the label
        const category = this.filterDisplay.find(cat => cat.categoryKey === categoryKey)

        if (category) {
          // Check if it's a slider by checking if it has a 'slider' property
          const isSlider = category.slider !== undefined
          if (isSlider) {
            // For sliders, show as a range from min to max
            // Sort the selected values by their position in category.items
            const sortedItems = selectedValues
              .map(v => category.items.find(i => String(i.value) === String(v)))
              .filter(item => item) // Remove any nulls
              .sort((a, b) => {
                const indexA = category.items.indexOf(a)
                const indexB = category.items.indexOf(b)
                return indexA - indexB
              })

            if (sortedItems.length > 0) {
              const minValue = formatNumberTextMaxDecimalPlaces(sortedItems[0].label)
              const maxValue = formatNumberTextMaxDecimalPlaces(sortedItems[sortedItems.length - 1].label)
              const rangeDisplay = minValue === maxValue ? [minValue] : [`${minValue} - ${maxValue}`]

              filters.push({
                key: categoryKey,
                label: category.categoryLabel,
                selectedValues: rangeDisplay,
              })
            }
          } else {
            // For non-sliders, show all selected values
            const valueLabels = selectedValues.map(value => {
              const item = category.items.find(i => String(i.value) === String(value))
              return item ? item.label : value
            })

            filters.push({
              key: categoryKey,
              label: category.categoryLabel,
              selectedValues: valueLabels,
            })
          }
        }
      })

      return filters
    },
    hasActiveConstraints() {
      if (!this.constraints) return false

      return (
        this.constraints.dateType
        && this.constraints.dateType !== 'None'
        && this.constraints.dateType !== 'none'
      )
    },
    constraintSummary() {
      if (!this.hasActiveConstraints) return ''

      const parts = []
      const typeLabels = {
        closingdate: 'Closing Date',
        strikedate: 'Strike Date',
        finalmarketdate: 'Final Market Date',
        Live: 'Live',
        Matured: 'Matured',
      }

      const dateType = typeLabels[this.constraints.dateType] || this.constraints.dateType

      if (this.constraints.dateFrom) {
        parts.push(`From: ${this.constraints.dateFrom}`)
      }
      if (this.constraints.dateTo) {
        parts.push(`To: ${this.constraints.dateTo}`)
      }

      return `${dateType} - ${parts.join(', ')}`
    },
  },
  mounted() {
    this.loadSelectionData()
  },
  activated() {
    // This hook is called every time the component is activated (including when kept-alive)
    this.loadSelectionData()
  },
  methods: {
    formatIsoDate,
    toggleReportStatus(reportId) {
      const index = this.disabledReports.indexOf(reportId)
      if (index > -1) {
        // Report is disabled, enable it
        this.disabledReports.splice(index, 1)
      } else {
        // Report is enabled, disable it
        this.disabledReports.push(reportId)
      }
    },
    isReportDisabled(reportId) {
      return this.disabledReports.includes(reportId)
    },
    loadSelectionData() {
      // Always load from sessionStorage
      const stored = sessionStorage.getItem('selectionRecordData')

      if (stored) {
        try {
          const data = JSON.parse(stored)

          // Check if this is new data by comparing report IDs
          const currentReportIds = this.reportIds.join(',')
          const newReportIds = (data.reportIds || []).join(',')
          const isNewSelection = currentReportIds !== newReportIds || this.search !== (data.search || '')

          // Only reset form fields if this is a new selection
          if (isNewSelection) {
            this.recordName = ''
            this.tableReason = ''
            this.filterReasons = {}
            this.constraintReason = ''
            this.disabledReports = []
          }

          this.reportIds = data.reportIds || []
          this.products = data.products || []
          this.excludedReportIds = data.excludedReportIds || []
          this.excludedProducts = data.excludedProducts || []
          this.filterData = data.filterData || {}
          this.search = data.search || ''
          this.filterDisplay = data.filterDisplay || []
          this.filterSequentialCounts = data.filterSequentialCounts || {}
          this.totalProductCount = data.totalProductCount || 0
          this.constraints = data.constraints || {}

          if (this.reportIds.length > 0) {
            this.error = false
            this.errorMessage = ''
            this.logSelectedReports()
          } else {
            this.error = true
            this.errorMessage = 'No reports selected. Please select reports from the Current Products page.'
          }
        } catch (e) {
          console.error('Failed to parse stored selection record data:', e)
          this.error = true
          this.errorMessage = 'Failed to load selection record data.'
        }
      } else {
        this.error = true
        this.errorMessage = 'No reports selected. Please select reports from the Current Products page.'
      }
    },
    logSelectedReports() {
      // Debug logging removed
    },
    saveSelectionRecord() {
      // Filter out disabled reports
      const activeReportIds = this.reportIds.filter(id => !this.disabledReports.includes(id))
      const activeProducts = this.products.filter(
        product => !this.disabledReports.includes(product.reportNumber),
      )

      // Collect all the data
      const selectionRecordData = {
        recordName: this.recordName,
        reportIds: activeReportIds,
        products: activeProducts,
        tableReason: this.tableReason,
        filterReasons: this.filterReasons,
        constraintReason: this.constraintReason,
        filterData: this.filterData,
        search: this.search,
        filterDisplay: this.filterDisplay,
        filterSequentialCounts: this.filterSequentialCounts,
        totalProductCount: this.totalProductCount,
        excludedProducts: this.excludedProducts,
        excludedReportIds: this.excludedReportIds,
        constraints: this.constraints,
      }

      // Store the data in sessionStorage for the generated record page
      sessionStorage.setItem('generatedRecordData', JSON.stringify(selectionRecordData))

      // Navigate to the generated record page
      this.$router.push({
        name: 'generated-record',
      })
    },
  },
}
</script>

<style scoped>
.report-row-disabled {
  opacity: 0.5;
}

/* Chevron rotation for collapse */
.collapsed .feather-icon[data-icon="ChevronDownIcon"] {
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

.not-collapsed .feather-icon[data-icon="ChevronDownIcon"] {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.small-textarea {
  font-size: 0.875rem;
}

.selected-values-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.filter-criteria-row {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 0.75rem;
  align-items: start;
}

.filter-criteria-left {
  min-width: 0;
}

.filter-criteria-right {
  align-self: start;
}

/* Scrollbar Styling */
.report-items::-webkit-scrollbar {
  width: 6px;
}

.report-items::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.report-items::-webkit-scrollbar-thumb {
  background: #c4c4c4;
  border-radius: 10px;
}

.report-items::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

</style>
